<template>
  <div class="app page-qrcode flex-row align-items-center">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-5 col-lg-4">
          <!-- OTP Entry Modal -->
          <b-modal
            v-if="enterOTP"
            v-model="enterOTP"
            title="Enter Authentication Code"
            size="md"
            hide-footer
            no-close-on-backdrop
            no-close-on-esc
            centered
            @hide="close"
          >
            <template #modal-header="{ titleClass }">
              <div class="d-flex align-items-center w-100">
                <i class="fa fa-lock text-primary mr-2 fa-lg" />
                <h5
                  :class="titleClass"
                  class="mb-0"
                >
                  Enter Authentication Code
                </h5>
              </div>
            </template>

            <div class="text-center mb-4">
              <div class="user-info mb-3">
                <div class="bg-light rounded p-3">
                  <i class="fa fa-user text-muted mr-2" />
                  <span class="text-muted">Authenticating as:</span>
                  <div class="mt-1">
                    <strong class="text-dark">{{ username }}</strong>
                  </div>
                </div>
              </div>
              
              <p class="mb-0 text-muted">
                Enter the 6-digit code from your authenticator app
              </p>
            </div>

            <form @submit.prevent="checkCreds">
              <div class="otp-input-section mb-4">
                <b-form-group>
                  <b-form-input
                    ref="otpInput"
                    v-model="otp"
                    type="tel"
                    inputmode="numeric"
                    pattern="[0-9]*"
                    maxlength="6"
                    class="form-control-lg text-center font-weight-bold"
                    :class="{ 'is-invalid': errorOTP }"
                    required
                    autofocus
                    placeholder="000000"
                    autocomplete="one-time-code"
                    @input="handleInput"
                    @paste="handlePaste"
                  />
                  <div class="mt-2">
                    <div
                      class="progress"
                      style="height: 4px;"
                    >
                      <div 
                        class="progress-bar" 
                        :style="{ width: progressWidth + '%' }"
                        :class="progressClass"
                      />
                    </div>
                  </div>
                </b-form-group>

                <b-alert
                  v-if="errorOTP"
                  show
                  variant="danger"
                  class="mb-0"
                >
                  <i class="fa fa-exclamation-triangle mr-2" />
                  {{ errorOTP }}
                </b-alert>
              </div>

              <div class="d-flex justify-content-between align-items-center">
                <b-button
                  variant="outline-secondary"
                  @click="close"
                >
                  <i class="fa fa-arrow-left mr-1" /> Back
                </b-button>
                
                <div class="text-center">
                  <small class="text-muted d-block mb-2">
                    Code auto-submits when complete
                  </small>
                </div>
                
                <b-button
                  type="submit"
                  variant="primary"
                  :disabled="otp.length < 6"
                >
                  <i class="fa fa-sign-in mr-1" /> Verify
                </b-button>
              </div>
            </form>
          </b-modal>
        </div>
      </div>
    </div>
    
    <footer class="login-footer">
      <img
        src="static/img/labs_logo.svg"
        class="labs-logo login center-block"
        alt="labs-img"
      >
    </footer>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as Routes from '../constants/routes'
import { APIEvent } from '../events/apiEvent'

export default {
  name: 'OTP',
  
  data() {
    return {
      enterOTP: true,
      otp: '',
      errorOTP: ''
    }
  },
  
  computed: {
    ...mapGetters([
      'username',
      'password', 
      'dynamics'
    ]),
    
    progressWidth() {
      return Math.min((this.otp.length / 6) * 100, 100)
    },
    
    progressClass() {
      if (this.otp.length === 0) return 'bg-secondary'
      if (this.otp.length < 6) return 'bg-warning'
      return 'bg-success'
    }
  },
  
  mounted() {
    this.$nextTick(() => {
      if (this.$refs.otpInput) {
        this.$refs.otpInput.focus()
      }
    })
  },
  
  methods: {
    close() {
      this.resetForm()
      this.$router.push(Routes.LOGIN)
    },
    
    resetForm() {
      this.otp = ''
      this.errorOTP = ''
    },
    
    login() {
      this.errorOTP = ''
      const { username, password, otp } = this
      this.$store.dispatch(APIEvent.LOGIN, { username, password, otp })
        .catch((error) => {
          this.handleError(error)
        })
    },
    
    checkCreds() {
      if (this.otp.length < 6) {
        return
      }
      
      this.errorOTP = ''
      const { username, password, otp } = this
      
      const options = {
        username,
        password,
        otp,
        dynamics: this.dynamics === 'yes' ? 'yes' : 'no'
      }

      this.$store.dispatch(APIEvent.CHECK_OTP, options)
        .catch((error) => {
          this.handleError(error)
        })
    },
    
    handleError(error) {
      this.otp = ''
      this.errorOTP = error.message || 'Authentication failed. Please try again.'
      this.$nextTick(() => {
        if (this.$refs.otpInput) {
          this.$refs.otpInput.focus()
        }
      })
    },
    
    handleInput(value) {
      // Remove any non-numeric characters
      const numericValue = value.replace(/\D/g, '')
      this.otp = numericValue.slice(0, 6)
      
      // Clear error when user starts typing
      if (this.errorOTP && this.otp.length > 0) {
        this.errorOTP = ''
      }
      
      // Auto-submit when complete
      if (this.otp.length === 6) {
        this.$nextTick(() => {
          this.checkCreds()
        })
      }
    },
    
    handlePaste(event) {
      event.preventDefault()
      const paste = (event.clipboardData || window.clipboardData).getData('text')
      const numericPaste = paste.replace(/\D/g, '').slice(0, 6)
      if (numericPaste) {
        this.otp = numericPaste
        if (numericPaste.length === 6) {
          this.$nextTick(() => {
            this.checkCreds()
          })
        }
      }
    }
  }
}
</script>
